import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import ReactLoading from "react-loading";
import CommonBtn from "../../components/buttons/commonBtn";
import { config } from "../../config";
import { StatusContext } from "../../contexts/Status.context";
import { UserInfoContext } from "../../contexts/UserInfo.context";
import { apiService } from "../../services/api.service";
import "./test.css";
import { toast } from "react-toastify";
import { FaTools, FaTimes } from "react-icons/fa";
import ReactPlayer from "react-player";
import useTranslation from "../../hooks/useTranslation";
import { getDateTime } from "../../utils";

const emotions = [
  "happiness",
  "contempt",
  "disgust",
  "anger",
  "sadness",
  "fear",
  "surprise",
  "neutral",
];

const Mechanics2: React.FC = () => {
  const {
    problems,
    problemNumber,
    setProblemNumber,
    metvProblems2,
    setMetvProblems,
  } = useContext(StatusContext);

  const {
    score,
    setScore,
    scoresDetail,
    setScoresDetail,
    problemScoreDetail,
    setProblemScoreDetail,
    userId,
    ip,
    email,
  } = useContext(UserInfoContext);

  const { t } = useTranslation();
  const videoAttributes = `playsinline autoplay muted`;

  const playerRef = useRef<ReactPlayer>(null);

  const [isStartTest, setIsStartTest] = useState(false);
  const [video, setVideo] = useState("");
  const [answer, setAnswer] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [trialNumber, setTrialNumber] = useState<number>(0);
  const [isNext, setIsNext] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [controlMode, setControlMode] = useState(false);

  const [isIPhone, setIsIPhone] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  const getProblem = useCallback(() => {
    setIsLoading(true);
    const problem = metvProblems2[problemNumber - 1];
    setProblemScoreDetail(problemScoreDetail + problem.answer + "~");
    if (problem) {
      if (isAndroid || isIPhone) {
        setVideo(problem.video);
      } else {
        setVideo(
          problem.video
            .replace("impact", "impact-hls")
            .replace("4khd", "4khd-hls")
            .replace(".mp4", ".m3u8")
        );
      }
      setAnswer(problem.answer);
    }
    setIsLoading(false);
  }, [metvProblems2, problemNumber]);

  useEffect(() => {
    if (problemNumber > 0) getProblem();
  }, [problemNumber]);

  useEffect(() => {
    handleVideoBlockHeight();
    setTimeout(() => {
      handleVideoBlockHeight();
    }, 1);
    window.addEventListener("resize", handleVideoBlockHeight);

    const userAgent = navigator.userAgent;
    setIsIPhone(/iPhone|iPod|iPad/.test(userAgent));
    setIsAndroid(/Android/.test(userAgent));

    return () => {
      window.removeEventListener("resize", handleVideoBlockHeight);
    };
  }, []);

  useEffect(() => {}, [isLoading]);

  const handleVideoBlockHeight = () => {
    let videoBlock = document.getElementById("video-block");
    let videoBlockWidth =
      document.getElementById("video-block")?.clientWidth || 0;
    if (videoBlock) {
      videoBlock.style.height = videoBlockWidth * 0.75 + 8 + "px";
    }
  };

  const handleStart = async () => {
    try {
      await handleStartScore();
      setIsStartTest(true);
      handleNextProblem();
    } catch (error) {
      toast.error("Failed to get problems.");
    }
  };

  const handleStartScore = async () => {
    try {
      const dateTime = getDateTime();
      const data = `score=${dateTime},${userId.replaceAll(
        ",",
        " "
      )},${email},${ip},Started,${module}`;
      const result = await apiService.saveMainScore(data);
    } catch (error) {}
  };

  const handleNextProblem = () => {
    setProblemNumber(problemNumber + 1);
    if (problemNumber > 0) handleScore();
    setAnswers([]);
    setIsNext(false);
    handleVideo("hideControls");
    setIsChecking(false);
    setTrialNumber(0);
    setIsLoading(true);
  };

  const handleAnswer = (emotion: string) => {
    let newAnswers: any;
    if (answers.find((item) => emotion === item)) {
      newAnswers = answers.filter((item) => item !== emotion);
    } else {
      if (answers.length < 3) {
        newAnswers = [...answers, emotion];
      } else {
        newAnswers = answers;
      }
    }
    setAnswers(newAnswers);
  };

  const handleTrial = () => {
    setIsChecking(true);
    setTrialNumber(trialNumber + 1);
  };

  const handleRetry = () => {
    setIsChecking(false);
    setAnswers([]);
    handleVideo("");
  };

  const handleScore = () => {
    let problemScore = 0;
    if (checkAnswer()) {
      if (trialNumber === 1) {
        problemScore = 5;
      } else if (trialNumber === 2) {
        problemScore = 1;
      }
    } else {
      problemScore = -2;
    }
    setScore(score + problemScore);
  };

  const checkStatus = (emotion: string) => {
    if (isChecking) {
      if (checkAnswer()) {
        if (answer.find((item) => emotion === item)) return 2;
        else return 0;
      } else {
        if (trialNumber < 3) {
          if (answers.find((item) => emotion === item)) {
            if (answer.find((item) => emotion === item)) return 2;
            else return 1;
          } else return 0;
        } else {
          if (answer.find((item) => emotion === item)) return 2;
          else return 1;
        }
      }
    } else {
      if (answers.find((item) => emotion === item)) return 4;
      else return 0;
    }
  };

  const handleVideo = (mode: string) => {
    const player = playerRef.current;
    if (mode === "showControls") {
      if (player) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer) {
          internalPlayer.controls = true;
        }
      }
    }
    if (mode === "hideControls") {
      if (player) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer) {
          internalPlayer.controls = false;
        }
      }
    }
    if (player) {
      const internalPlayer = player.getInternalPlayer();
      if (internalPlayer) {
        internalPlayer.currentTime = 0; // Seek to the beginning
        internalPlayer.play(); // Play the video
      }
    }
  };

  const checkAnswer = () => {
    if (answers.sort().join(",") == answer.sort().join(",")) {
      return true;
    } else {
      return false;
    }
  };
  const getProblemState = () => {
    if (!isChecking) {
      return {
        text: t("Which emotion(s) does this person experience?"),
        color: "white",
      };
    } else if (checkAnswer()) {
      handleVideo("showControls");
      return {
        text: t("Correct! Replay the video or click next"),
        color: "green",
      };
    } else if (trialNumber < 3) {
      return {
        text: `${t("Attempts left")}: ${3 - trialNumber}. ${t("Try again")}`,
        color: "red",
      };
    } else {
      handleVideo("showControls");
      return {
        text: t("Correct answer: marked green Replay or click next"),
        color: "white",
      };
    }
  };

  const handleReadyVideo = (e: any) => {
    setIsLoading(false);
    const player = playerRef.current;
    if (player) {
      const internalPlayer = player.getInternalPlayer();
      if (internalPlayer) {
        internalPlayer.currentTime = 0; // Seek to the beginning
        internalPlayer.play(); // Play the video
      }
    }
  };

  return (
    <div className="block lg:flex lg:w-[80%] xl:w-[60%] lg:mx-auto">
      <div className="lg:w-[70%]">
        <div className="relative">
          {isAndroid || isIPhone ? (
            <div className="mt-2 w-[97%] md:w-[70%] lg:w-full border-4 border-gray-500 rounded mb-8 mx-auto bg-gray-600 min-h-[200px]">
              <div className="w-full pt-[75%] relative">
                <div className="absolute top-0 left-0 right-0">
                  {video != "" && (
                    <ReactPlayer
                      ref={playerRef}
                      url={(isStartTest && config.awsS3CDN + video) || ""}
                      controls={false}
                      width="100%"
                      height="100%"
                      config={{
                        file: {
                          attributes: {
                            controlsList: "nodownload",
                            disablePictureInPicture: true,
                          },
                        },
                      }}
                      playing={false}
                      playsinline={true}
                      preload="metadata"
                      onReady={(e) => handleReadyVideo(e)}
                      muted
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-2 w-[97%] md:w-[70%] lg:w-full border-4 border-gray-500 rounded mb-8 mx-auto bg-gray-600 min-h-[200px]">
              <div className="w-full pt-[75%] relative">
                <div className="absolute top-0 left-0 right-0">
                  {video != "" && (
                    <ReactPlayer
                      ref={playerRef}
                      url={(isStartTest && config.awsS3CDN + video) || ""}
                      controls={false}
                      width="100%"
                      height="100%"
                      config={{
                        file: {
                          forceHLS: true,
                          hlsOptions: {
                            bufferSize: 30,
                          },
                        },
                      }}
                      playing={false}
                      playsinline={true}
                      preload="metadata"
                      onReady={(e) => handleReadyVideo(e)}
                      muted
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {isLoading && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <ReactLoading type="spokes" />
            </div>
          )}
        </div>
        {!isStartTest ? (
          <div className="w-[70%] lg:w-full text-center mx-auto">
            <CommonBtn status={3} onClick={handleStart}>
              {t("Click to start")}
            </CommonBtn>
          </div>
        ) : (
          <div>
            <div className="problem-state w-[70%] lg:w-full mx-auto">
              {problemNumber}/20&nbsp;&nbsp;&nbsp;
              <span style={{ color: getProblemState()?.color }}>
                {getProblemState()?.text}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="lg:w-[30%] mt-4 lg:mt-0">
        {isStartTest && (
          <div className="grid grid-cols-2 w-[70%] mx-auto">
            {emotions.map((emotion: string, index: number) => (
              <div
                key={index}
                className="mb-2 mx-1 col-span-1 lg:col-span-2 flex justify-center lg:justify-start"
              >
                <CommonBtn
                  status={checkStatus(emotion)}
                  onClick={() => handleAnswer(emotion)}
                  disabled={isNext || isLoading || isChecking}
                >
                  {t(emotion)}
                </CommonBtn>
              </div>
            ))}

            {!isChecking ? (
              answers.length > 0 ? (
                <div className="mt-2 flex justify-center col-span-2 lg:justify-start">
                  <CommonBtn status={3} onClick={handleTrial}>
                    {t("next")}
                  </CommonBtn>
                </div>
              ) : (
                <div className="md:hidden h-[57px]"></div>
              )
            ) : (
              <div className="mt-2 flex justify-center col-span-2 lg:justify-start">
                <CommonBtn
                  status={3}
                  onClick={() => {
                    if (checkAnswer() || trialNumber === 3) {
                      handleNextProblem();
                    } else {
                      handleRetry();
                    }
                  }}
                >
                  {checkAnswer() || trialNumber === 3 ? t("next") : t("retry")}
                </CommonBtn>
              </div>
            )}
          </div>
        )}
      </div>
      {isStartTest && (checkAnswer() || trialNumber === 3) && (
        <div className="fixed top-1/2 right-0">
          <button
            className={`solution-btn`}
            onClick={() => setShowSolution(true)}
          >
            <FaTools />
          </button>
        </div>
      )}
      {isStartTest && showSolution && (
        <div className="fixed bg-gray-900 bg-opacity-60 top-0 left-0 right-0 bottom-0 overflow-auto flex items-center">
          <div className="grid grid-cols-2 w-[80%] mx-auto bg-white pt-3 relative">
            <div className="p-2 col-span-2 md:col-span-1 pb-12">
              <div className="text-center text-black text-lg font-bold">
                {t("Original video")}:
              </div>
              <ReactPlayer
                url={(isStartTest && config.awsS3CDN + video) || ""}
                controls={true}
                width="100%"
                height="100%"
                config={{
                  file: {
                    forceHLS: true,
                    hlsOptions: {
                      bufferSize: 30,
                    },
                  },
                }}
                playing={true}
                preload="metadata"
              />
            </div>
            <div className="p-2 col-span-2 md:col-span-1 pb-12">
              <div className="text-center text-black text-lg font-bold">
                {t("Solution")}:
              </div>
              <ReactPlayer
                url={
                  (isStartTest &&
                    config.awsS3CDN + video.replace(".m3u8", "-s.m3u8")) ||
                  ""
                }
                controls={true}
                width="100%"
                height="100%"
                config={{
                  file: {
                    forceHLS: true,
                    hlsOptions: {
                      bufferSize: 30,
                    },
                  },
                }}
                playing={true}
                preload="metadata"
              />
            </div>
            <div
              className="absolute top-2 right-2 text-black text-2xl cursor-pointer"
              onClick={() => setShowSolution(false)}
            >
              <FaTimes />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mechanics2;
